import React, { useEffect, useRef, useState } from 'react'
import Icon from '../Icon'
import { TOOLS } from '../../data/tools'

function Presets({ options, value, onPick }) {
  return (
    <div className="presets">
      {options.map((o) => (
        <button key={o} className={'preset' + (value === o ? ' active' : '')} onClick={() => onPick(o)}>
          {o}
        </button>
      ))}
    </div>
  )
}

// Password input with a show/hide eye toggle.
function PasswordField({ value, placeholder, onChange }) {
  const [show, setShow] = useState(false)
  return (
    <div style={{ position: 'relative' }}>
      <input
        className="in"
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={{ paddingRight: 42 }}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? 'Hide password' : 'Show password'}
        style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--faint)', display: 'grid', placeItems: 'center', padding: 6 }}
      >
        <Icon name={show ? 'eye-off' : 'eye'} size={17} />
      </button>
    </div>
  )
}

function SignaturePad({ onChange }) {
  const ref = useRef(null)
  const drawing = useRef(false)
  const last = useRef(null)

  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas.getContext('2d')
    ctx.lineWidth = 4
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#14181F'
  }, [])

  // Map the pointer to canvas pixels — the canvas is drawn at 600x200 internally
  // but displayed at a different CSS width, so scale by that ratio or the
  // strokes won't follow the cursor.
  const pos = (e) => {
    const canvas = ref.current
    const r = canvas.getBoundingClientRect()
    const t = e.touches ? e.touches[0] : e
    return {
      x: (t.clientX - r.left) * (canvas.width / r.width),
      y: (t.clientY - r.top) * (canvas.height / r.height),
    }
  }
  const start = (e) => {
    e.preventDefault()
    drawing.current = true
    last.current = pos(e)
  }
  const move = (e) => {
    if (!drawing.current) return
    e.preventDefault()
    const ctx = ref.current.getContext('2d')
    const p = pos(e)
    ctx.beginPath()
    ctx.moveTo(last.current.x, last.current.y)
    ctx.lineTo(p.x, p.y)
    ctx.stroke()
    last.current = p
  }
  const end = () => {
    if (!drawing.current) return
    drawing.current = false
    onChange(ref.current.toDataURL('image/png'))
  }
  const clear = () => {
    const ctx = ref.current.getContext('2d')
    ctx.clearRect(0, 0, ref.current.width, ref.current.height)
    onChange(null)
  }

  return (
    <div>
      <canvas
        ref={ref}
        width={600}
        height={200}
        style={{ width: '100%', height: 130, border: '1px solid var(--line)', borderRadius: 10, background: '#fff', touchAction: 'none', cursor: 'crosshair' }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <button className="btn btn-soft" style={{ marginTop: 10, padding: '8px 14px', fontSize: 13 }} onClick={clear}>
        Clear signature
      </button>
    </div>
  )
}

function ToolOptions({ id, opts, setOpt, meta }) {
  const type = TOOLS[id].opts
  const ratio = meta ? meta.w / meta.h : 1

  const Title = <div className="opts-title">Settings</div>

  switch (type) {
    case 'quality':
      return (
        <>
          {Title}
          <div className="field">
            <label>
              Compression
              <span className="range-val">
                {opts.quality > 85 ? 'High quality · ' : opts.quality < 45 ? 'Smallest size · ' : 'Recommended · '}
                {opts.quality}%
              </span>
            </label>
            <input type="range" min="20" max="95" value={opts.quality} onChange={(e) => setOpt('quality', +e.target.value)} />
          </div>
        </>
      )
    case 'dims':
      return (
        <>
          {Title}
          <div className="field">
            <label>New dimensions <span className="hint">in pixels</span></label>
            <div className="dims">
              <input
                type="number"
                min="1"
                placeholder="Width"
                value={opts.width || ''}
                onChange={(e) => {
                  const w = +e.target.value
                  setOpt('width', w)
                  if (opts.lock && w) setOpt('height', Math.round(w / ratio))
                }}
              />
              <span className="xsign">×</span>
              <input
                type="number"
                min="1"
                placeholder="Height"
                value={opts.height || ''}
                onChange={(e) => {
                  const h = +e.target.value
                  setOpt('height', h)
                  if (opts.lock && h) setOpt('width', Math.round(h * ratio))
                }}
              />
            </div>
            <label className="lockrow">
              <input type="checkbox" checked={!!opts.lock} onChange={(e) => setOpt('lock', e.target.checked)} />
              Lock aspect ratio
            </label>
          </div>
        </>
      )
    case 'crop':
      return (
        <>
          {Title}
          <div className="field">
            <label>Aspect ratio</label>
            <div className="presets">
              {[
                ['Freeform', undefined],
                ['1:1', 1],
                ['4:3', 4 / 3],
                ['16:9', 16 / 9],
                ['9:16', 9 / 16],
              ].map(([label, val]) => (
                <button key={label} className={'preset' + (opts.aspect?.label === label ? ' active' : '')} onClick={() => setOpt('aspect', { value: val, label })}>
                  {label}
                </button>
              ))}
            </div>
            <div className="hint" style={{ marginTop: 10 }}>Drag the handles on the image to choose the area.</div>
          </div>
        </>
      )
    case 'imgformat':
      return (
        <>
          {Title}
          <div className="field">
            <label>Convert to</label>
            <select className="in" value={opts.format} onChange={(e) => setOpt('format', e.target.value)}>
              <option>PNG</option>
              <option>JPG</option>
              <option>WebP</option>
            </select>
          </div>
        </>
      )
    case 'removebg':
      return (
        <>
          <div className="opts-title">Ready to go</div>
          <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.55 }}>
            An AI model removes the background automatically and keeps your subject
            in full colour and detail no settings needed.
            <div className="hint" style={{ marginTop: 8 }}>
              First run downloads the model, so it may take a few seconds.
            </div>
          </div>
        </>
      )
    case 'blurface':
      return (
        <>
          {Title}
          <div className="field">
            <label>Blur strength <span className="range-val">{opts.strength}</span></label>
            <input type="range" min="6" max="40" value={opts.strength} onChange={(e) => setOpt('strength', +e.target.value)} />
            <div className="hint">Drag boxes over the faces / areas on the image, then apply.</div>
          </div>
        </>
      )
    case 'rotate':
      return (
        <>
          {Title}
          <div className="field">
            <label>Transform</label>
            <Presets options={['Rotate left', 'Rotate right', 'Flip H', 'Flip V']} value={opts.transform} onPick={(v) => setOpt('transform', v)} />
          </div>
        </>
      )
    case 'scale':
      return (
        <>
          {Title}
          <div className="field">
            <label>Enlarge by</label>
            <Presets options={['2x', '4x', '6x']} value={opts.scale + 'x'} onPick={(v) => setOpt('scale', v.replace('x', ''))} />
          </div>
        </>
      )
    case 'watermark':
      return (
        <>
          {Title}
          <div className="field">
            <label>Watermark text</label>
            <input className="in" type="text" placeholder="© Your name" value={opts.text} onChange={(e) => setOpt('text', e.target.value)} />
          </div>
          <div className="field">
            <label>Position</label>
            <Presets options={['Top-left', 'Center', 'Tiled']} value={opts.position} onPick={(v) => setOpt('position', v)} />
          </div>
        </>
      )
    case 'pagesize':
      return (
        <>
          {Title}
          <div className="field">
            <label>Page size</label>
            <select className="in" value={opts.pagesize} onChange={(e) => setOpt('pagesize', e.target.value)}>
              <option>A4</option>
              <option>Letter</option>
              <option>Fit to image</option>
            </select>
          </div>
          <div className="field">
            <label>Orientation</label>
            <Presets options={['Portrait', 'Landscape']} value={opts.orientation} onPick={(v) => setOpt('orientation', v)} />
          </div>
        </>
      )
    case 'reorder':
      return (
        <>
          {Title}
          <div className="field">
            <label>Order</label>
            <div className="hint" style={{ fontWeight: 400 }}>Files merge top to bottom remove any you don't need above.</div>
          </div>
        </>
      )
    case 'pages':
      return (
        <>
          {Title}
          <div className="field">
            <label>Pages to extract <span className="hint">e.g. 1-3, 5, 8</span></label>
            <input className="in" type="text" placeholder="1-3, 5" value={opts.pages} onChange={(e) => setOpt('pages', e.target.value)} />
            <div className="hint" style={{ marginTop: 8 }}>Leave blank to keep every page.</div>
          </div>
        </>
      )
    case 'level':
      return (
        <>
          {Title}
          <div className="field">
            <label>Compression level</label>
            <Presets options={['Light', 'Recommended', 'Strong']} value={opts.level} onPick={(v) => setOpt('level', v)} />
          </div>
        </>
      )
    case 'imgquality':
      return (
        <>
          {Title}
          <div className="field">
            <label>Image quality</label>
            <Presets options={['Standard', 'High', 'Max']} value={opts.quality} onPick={(v) => setOpt('quality', v)} />
          </div>
        </>
      )
    case 'editpdf':
      return (
        <>
          {Title}
          <div className="field">
            <label>Text to add</label>
            <input className="in" type="text" placeholder="Your text" value={opts.text} onChange={(e) => setOpt('text', e.target.value)} />
          </div>
          <div className="field">
            <label>Page</label>
            <input className="in" type="number" min="1" value={opts.page} onChange={(e) => setOpt('page', e.target.value)} />
          </div>
          <div className="field">
            <label>Position</label>
            <Presets options={['Top', 'Center', 'Bottom']} value={opts.position} onPick={(v) => setOpt('position', v)} />
          </div>
        </>
      )
    case 'sign':
      return (
        <>
          {Title}
          <div className="field">
            <label>Draw your signature</label>
            <SignaturePad onChange={(d) => setOpt('signature', d)} />
            <div className="hint" style={{ marginTop: 8 }}>
              After drawing, drag it on the page preview to position it, and pull the
              corner handle to resize.
            </div>
          </div>
        </>
      )
    case 'password':
      return (
        <>
          {Title}
          <div className="field">
            <label>Set a password</label>
            <PasswordField placeholder="Enter a strong password" value={opts.password} onChange={(e) => setOpt('password', e.target.value)} />
          </div>
          <div className="field">
            <label>Confirm password</label>
            <PasswordField placeholder="Re-enter password" value={opts.confirm} onChange={(e) => setOpt('confirm', e.target.value)} />
          </div>
        </>
      )
    case 'unlock':
      return (
        <>
          {Title}
          <div className="field">
            <label>PDF password</label>
            <PasswordField placeholder="Enter the current password" value={opts.password} onChange={(e) => setOpt('password', e.target.value)} />
            <div className="hint" style={{ marginTop: 8 }}>You must know the password this removes it, it doesn't crack it.</div>
          </div>
        </>
      )
    case 'wmpdf':
      return (
        <>
          {Title}
          <div className="field">
            <label>Watermark text</label>
            <input className="in" type="text" placeholder="CONFIDENTIAL" value={opts.text} onChange={(e) => setOpt('text', e.target.value)} />
          </div>
          <div className="field">
            <label>Style</label>
            <Presets options={['Diagonal', 'Tiled']} value={opts.position} onPick={(v) => setOpt('position', v)} />
          </div>
        </>
      )
    case 'rotatepdf':
      return (
        <>
          {Title}
          <div className="field">
            <label>Rotate pages</label>
            <Presets options={['90° right', '180°', '270° left']} value={opts.angle} onPick={(v) => setOpt('angle', v)} />
          </div>
        </>
      )
    case 'pagenum':
      return (
        <>
          {Title}
          <div className="field">
            <label>Format</label>
            <Presets options={['Number', 'Page X of N']} value={opts.format} onPick={(v) => setOpt('format', v)} />
          </div>
          <div className="field">
            <label>Position</label>
            <Presets options={['Bottom center', 'Bottom right', 'Top center', 'Top right']} value={opts.position} onPick={(v) => setOpt('position', v)} />
          </div>
        </>
      )
    default:
      return (
        <>
          <div className="opts-title">Ready to go</div>
          <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.5 }}>
            No setup needed just run the tool and your file will be processed automatically.
          </div>
        </>
      )
  }
}

export default ToolOptions
